import React, { createContext, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

// context
import { useAppContext } from "./AppContext";

// services
import useAnalyticsService from "../../services/analytics.service";
import usePlatformService from "../../services/platform.service";
import TokenService from "../../services/token.service";

// components
import Loader from "../components/LoadingScreen";

// utils
import { isIframe, platformConfig } from "../utils";

const ProjectContext = createContext();

export const PlatformProvider = ({ children }) => {
    const {
        platform,
        setPlatform,
        projectKey,
        setProjectKey,
        authTokens,
        setAuthTokens,
        apiReady,
        setNotificationMessage,
    } = useAppContext();
    const [shouldLogin, setShouldLogin] = useState(TokenService.getLoginFlag());
    const [isCoursewareProject, setIsCoursewareProject] = useState(false);
    const [challengeList, setChallengeList] = useState([]);
    const [userPoints, setUserPoints] = useState(null);
    const [loginRequired, setLoginRequired] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [isDoneLoading, setIsDoneLoading] = useState(false);
    const [project, setProject] = useState(null);
    const [user, setUser] = useState(null);

    const PlatformService = usePlatformService();
    const AnalyticsService = useAnalyticsService();

    useEffect(() => {
        if (isIframe()) {
            handleCourseware();
        } else {
            handleAuthentication();
        }
    }, []);

    useEffect(() => {
        if (authTokens && apiReady && !user) {
            getAuthUser();
        }
    }, [authTokens, apiReady]);

    useEffect(() => {
        if (user && apiReady) {
            AnalyticsService.pingSession();
            getChallengeList();
            if (platform) {
                getUserPoints();
            }
            if (!projectKey) {
                setIsDoneLoading(true);
            } else {
                getProjectData();
            }
        }
    }, [user, apiReady, projectKey, platform]);

    useEffect(() => {
        TokenService.setLoginFlag(shouldLogin);
    }, [shouldLogin]);

    useEffect(() => {
        if (project) {
            setIsDoneLoading(true);
        }
    }, [project]);

    useEffect(() => {
        TokenService.setProjectId(projectKey);
    }, [projectKey]);

    useEffect(() => {
        if (platform) {
            TokenService.setPlatform(platform);
        }
    }, [platform]);

    const deleteUrlParams = (params) => {
        params.forEach((param) => {
            searchParams.delete(param);
        });
        setSearchParams();
    };

    const handleCourseware = () => {
        AnalyticsService.setCoursewareEmbedMode();
        setIsCoursewareProject(true);
        setProject({
            content: `<xml xmlns="https://developers.google.com/blockly/xml"></xml>`,
        });
        setIsDoneLoading(true);
    };

    const handleAuthentication = async () => {
        // check for url parameters
        const accessTokenParam = searchParams.get("access_token");
        const ide_token = searchParams.get("ide_token");
        const platformName = searchParams.get("platform");
        const projectId = searchParams.get("project");
        // validate access token
        if (accessTokenParam) {
            deleteUrlParams(["access_token"]);
            verifyAccessToken(accessTokenParam);
        }
        // save platform, project key and validate ide token
        else if (platformName && projectId && ide_token) {
            deleteUrlParams(["pk", "platform", "ide_token"]);
            setPlatform(platformName);
            setProjectKey(projectId);
            verifyIdeToken(platformName, ide_token);
        }
        // login to sso
        else {
            if (projectId || projectKey || shouldLogin) {
                loginToSSO();
            } else {
                AnalyticsService.pingSession();
                setIsDoneLoading(true);
            }
        }
    };

    const verifyAccessToken = async (access_token) => {
        let { status, data } = await PlatformService.verifyRequestToken(
            access_token
        );
        if (status) {
            setAuthTokens({
                access: data.access,
                refresh: data.refresh,
                csrf: data.csrf,
            });
        } else {
            handleFailedPlatformRequest(data);
        }
    };

    const verifyIdeToken = async (platform, ide_token) => {
        let platformTag = platformConfig[platform]?.subdomain || platform;
        let { status, data } = await PlatformService.verifyIdeToken(
            platformTag,
            ide_token
        );
        if (status) {
            window.location.replace(data.sso_url);
        } else {
            handleFailedPlatformRequest(data);
        }
    };

    const getAuthUser = async () => {
        let { status, data } = await PlatformService.getAuthUser();
        if (status && data) {
            setUser(data);
            setShouldLogin(true);
        } else {
            console.error(
                "Failed to fetch authenticated user data:",
                data.message,
                "Status:",
                status
            );
            loginToSSO();
        }
    };

    const logoutUser = async () => {
        let { status } = await PlatformService.logoutUser();
        if (status) {
            setPlatform(null);
            setProjectKey(null);
            setShouldLogin(null);
            TokenService.setSessionId(null);
            window.location.reload();
        } else {
            setNotificationMessage({
                message:
                    "Logout failed. Please try again later or contact Resolute Support",
                type: "error",
                duration: 4000,
            });
        }
    };

    const loginToSSO = async () => {
        let { status, data } = await PlatformService.getSSOurl(
            window.location.pathname
        );
        if (status && data.sso_url) {
            window.location.replace(data.sso_url);
        } else {
            setNotificationMessage({
                message:
                    "Login failed. Please try again later or contact Resolute Support",
                type: "error",
                duration: 6000,
            });
        }
    };

    const getProjectData = async () => {
        if (!projectKey) {
            return;
        }
        let { status, data } = await PlatformService.getProject(
            platform.toLowerCase(),
            projectKey
        );
        if (status) {
            setProject(data);
        } else {
            window.setTimeout(() => {
                PlatformService.redirectToPlatform(platform);
            }, 3000);
        }
    };

    const updateProject = async (projectData) => {
        let { status, data } = await PlatformService.updateProject(
            platform.toLowerCase(),
            projectKey,
            project.title,
            projectData
        );
        if (status) {
            setProject({
                pk: projectKey,
                title: project.title,
                content: data.content,
            });
        } else {
            handleFailedPlatformRequest(data);
        }
    };

    const getUserPoints = async () => {
        let { status, data } = await PlatformService.getUserPoints(
            platform.toLowerCase()
        );
        if (status) {
            setUserPoints(data.points);
        } else {
            handleFailedPlatformRequest(data);
        }
    };

    // Action Type is "open" or "upload"
    const setIdeAction = async (actionType) => {
        if (!user || !platform) return;
        let { status, data } = await PlatformService.setIDEAction(
            platform.toLowerCase(),
            actionType
        );
        if (status) {
            getUserPoints();
        } else {
            handleFailedPlatformRequest(data);
        }
    };

    const getChallengeList = async () => {
        let { status, data } = await PlatformService.getChallengeList();
        if (status) {
            setChallengeList(data);
        } else {
            handleFailedPlatformRequest(data);
        }
    };

    const getCoursewareChallengeData = async (challengeId) => {
        let { status, data } = await PlatformService.getCoursewareChallengeData(
            challengeId
        );
        if (status) {
            return data;
        } else {
            handleFailedPlatformRequest(data);
        }
    };

    const getChallengeData = async (challengeId) => {
        let { status, data } = await PlatformService.getChallengeData(
            challengeId
        );
        if (status) {
            return data;
        } else {
            handleFailedPlatformRequest(data);
        }
    };

    const setChallengeProgress = async (challengeId, activity) => {
        let { status, data } = await PlatformService.setChallengeProgress(
            challengeId,
            activity
        );
        if (status) {
            return data;
        } else {
            handleFailedPlatformRequest(data);
        }
    };

    const handleFailedPlatformRequest = ({ code, message }) => {
        if (code === 403 || code === 401) {
            setAuthTokens(null);
            setLoginRequired(true);
        } else {
            throw new Error(message);
        }
    };

    let providerValue = {
        user,
        userPoints,
        project,
        setProjectKey,
        projectKey,
        updateProject,
        platform,
        setPlatform,
        authTokens,
        setIdeAction,
        loginToSSO,
        logoutUser,
        loginRequired,
        isCoursewareProject,
        challengeList,
        setChallengeList,
        getChallengeData,
        getCoursewareChallengeData,
        setChallengeProgress,
    };

    if (!isDoneLoading) {
        return <Loader />;
    }

    return (
        <ProjectContext.Provider value={providerValue}>
            {children}
        </ProjectContext.Provider>
    );
};

const usePlatformContext = () => {
    return useContext(ProjectContext);
};

export { usePlatformContext };
